// File: src/components/inventory/ItemDetailModal.tsx

import React, { useState, useEffect } from 'react';
import { X, Edit, Trash2, Package, Calendar, MapPin, User, Shield, AlertTriangle, Clock, RotateCcw } from 'lucide-react';
import { InventoryItem as APIInventoryItem } from '../../services/inventoryAPI';
import { Assignment } from '../../types/assignment';

interface ItemDetailModalProps {
  isOpen: boolean;
  item: APIInventoryItem | null;
  onClose: () => void;
  onEdit: (item: APIInventoryItem) => void;
  onDelete: (item: APIInventoryItem) => void;
}

const ItemDetailModal: React.FC<ItemDetailModalProps> = ({ 
  isOpen, 
  item, 
  onClose, 
  onEdit, 
  onDelete 
}) => {
  const [assignmentHistory, setAssignmentHistory] = useState<Assignment[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [assignmentSystemAvailable, setAssignmentSystemAvailable] = useState(false);

  useEffect(() => {
    if (isOpen && item) {
      loadAssignmentHistory();
    }
  }, [isOpen, item]);

  const loadAssignmentHistory = async () => {
    if (!item) return;
    
    try {
      setLoadingHistory(true);
      
      // Try to load assignment history - if assignment system is available
      try {
        const { assignmentAPI } = await import('../../services/assignmentAPI');
        const response = await assignmentAPI.getItemHistoryCompat(item.itemId);
        setAssignmentHistory(response.sort((a, b) => 
          new Date(b.assignmentDate).getTime() - new Date(a.assignmentDate).getTime()
        ));
        setAssignmentSystemAvailable(true);
      } catch (err) {
        // Assignment system not available yet
        console.log('Assignment system not available yet');
        setAssignmentSystemAvailable(false);
        setAssignmentHistory([]);
      }
    } catch (err) {
      console.error('Error loading assignment history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  if (!isOpen || !item) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Available': return 'bg-green-100 text-green-800 border-green-200';
      case 'Assigned': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Maintenance': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Retired': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'Excellent': return 'text-green-700';
      case 'Good': return 'text-blue-700';
      case 'Fair': return 'text-yellow-700';
      case 'Poor': return 'text-orange-700';
      case 'Needs Repair': return 'text-red-700';
      default: return 'text-gray-700';
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${item.name}"? This action cannot be undone.`)) {
      onDelete(item);
    }
  };

  // Check if body armor is expiring soon (within 6 months)
  const isBodyArmorExpiringSoon = () => {
    if (!('bodyArmor' in item) || !(item as any).bodyArmor) return false;
    
    const expirationDate = new Date((item as any).bodyArmor.expirationDate);
    const sixMonthsFromNow = new Date();
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
    
    return expirationDate <= sixMonthsFromNow;
  };

  const getCurrentAssignment = () => {
    return assignmentHistory.find(assignment => assignment.status === 'Active');
  };

  const currentAssignment = getCurrentAssignment();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Package className="w-6 h-6 mr-3" style={{ color: '#082759' }} />
            <div>
              <h2 className="text-xl font-bold text-gray-800">{item.name}</h2>
              <p className="text-sm text-gray-600">{item.category}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEdit(item)}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Edit Item"
            >
              <Edit className="w-5 h-5" />
            </button>
            <button
              onClick={handleDelete}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete Item"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body Armor Expiration Warning */}
        {isBodyArmorExpiringSoon() && (
          <div className="mx-6 mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Expiration Warning</h3>
                <p className="text-sm text-yellow-700">
                  This body armor expires on {formatDate((item as any).bodyArmor?.expirationDate)}. 
                  Please schedule replacement soon.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="p-6 space-y-6">
          {/* Status & Condition Bar */}
          <div className="flex flex-wrap gap-3">
            {item.itemType === 'SERIALIZED' && (item as any).status && (
              <div className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor((item as any).status)}`}>
                {(item as any).status}
              </div>
            )}
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${getConditionColor(item.condition)}`}>
              {item.condition} Condition
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              item.itemType === 'SERIALIZED' 
                ? 'bg-blue-100 text-blue-800'
                : item.itemType === 'CONSUMABLE'
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-green-100 text-green-800'
            }`}>
              {item.itemType === 'SERIALIZED' 
                ? 'Serialized' 
                : item.itemType === 'CONSUMABLE'
                  ? 'Consumable'
                  : 'Bulk Tracked'}
            </div>
          </div>

          {/* Main Information Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Basic Information</h3>
              
              {item.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-600">Description</label>
                  <p className="text-gray-800">{item.description}</p>
                </div>
              )}

              {item.manufacturer && (
                <div>
                  <label className="block text-sm font-medium text-gray-600">Manufacturer</label>
                  <p className="text-gray-800">{item.manufacturer}</p>
                </div>
              )}

              {item.model && (
                <div>
                  <label className="block text-sm font-medium text-gray-600">Model</label>
                  <p className="text-gray-800">{item.model}</p>
                </div>
              )}

              {item.itemType === 'SERIALIZED' && (item as any).serialNumber && (
                <div>
                  <label className="block text-sm font-medium text-gray-600">Serial Number</label>
                  <p className="text-gray-800 font-mono">{(item as any).serialNumber}</p>
                </div>
              )}

              {item.itemType !== 'SERIALIZED' && (
                <div className="grid grid-cols-2 gap-4">
                  {/* Only show Total Quantity for BULK_TRACKED, not CONSUMABLE */}
                  {item.itemType !== 'CONSUMABLE' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Total Quantity</label>
                      <p className="text-gray-800">{(item as any).totalQuantity}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      {item.itemType === 'CONSUMABLE' ? 'Quantity On Hand' : 'Available'}
                    </label>
                    <p className="text-gray-800">{(item as any).availableQuantity}</p>
                  </div>
                  {(item as any).minStockLevel && (
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Min Stock Level</label>
                      <p className="text-gray-800">{(item as any).minStockLevel}</p>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Location
                </label>
                <p className="text-gray-800">
                  {item.location}
                  {item.subLocation && (
                    <span className="text-gray-500"> → {item.subLocation}</span>
                  )}
                </p>
              </div>
            </div>

            {/* Right Column - Assignment Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Assignment Information</h3>
              
              {/* Current Assignment - New Assignment System */}
              {assignmentSystemAvailable && currentAssignment ? (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Currently Assigned
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium text-blue-700">Officer:</span> {currentAssignment.assignedToUserName}
                    </div>
                    <div>
                      <span className="font-medium text-blue-700">Assigned Date:</span> {formatDate(currentAssignment.assignmentDate)}
                    </div>
                    <div>
                      <span className="font-medium text-blue-700">Assigned By:</span> {currentAssignment.assignedByUserId}
                    </div>
                    <div>
                      <span className="font-medium text-blue-700">Reason:</span> {currentAssignment.assignmentReason}
                    </div>
                  </div>
                </div>
              ) : (
                /* Legacy Assignment Info or Not Assigned */
                item.itemType === 'SERIALIZED' && (item as any).assignedTo ? (
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <h4 className="font-medium text-amber-900 mb-2 flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      Currently Assigned
                    </h4>
                    <div className="space-y-1 text-sm">
                      <div>
                        <span className="font-medium text-amber-700">Officer:</span> {(item as any).assignedTo}
                      </div>
                      {(item as any).assignedDate && (
                        <div>
                          <span className="font-medium text-amber-700">Date:</span> {formatDate((item as any).assignedDate)}
                        </div>
                      )}
                      {(item as any).assignedBy && (
                        <div>
                          <span className="font-medium text-amber-700">Assigned By:</span> {(item as any).assignedBy}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-600 text-center">
                      <Package className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      Not Currently Assigned
                    </p>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Assignment History - FIXED: Now shows return information */}
          {assignmentSystemAvailable && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                Assignment History
              </h3>
              
              {loadingHistory ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : assignmentHistory.length > 0 ? (
                <div className="space-y-3">
                  {assignmentHistory.map((assignment, index) => (
                    <div 
                      key={assignment.assignmentId}
                      className={`p-4 rounded-lg border-l-4 ${
                        assignment.status === 'Active' 
                          ? 'bg-blue-50 border-blue-400' 
                          : assignment.status === 'Returned'
                          ? 'bg-green-50 border-green-400'
                          : 'bg-white border-gray-300'
                      }`}
                    >
                      {/* Assignment Header */}
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-medium">
                          {assignment.assignedToUserName}
                          {assignment.status === 'Active' && (
                            <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              Active
                            </span>
                          )}
                          {assignment.status === 'Returned' && (
                            <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center">
                              <RotateCcw className="w-3 h-3 mr-1" />
                              Returned
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(assignment.assignmentDate)}
                        </div>
                      </div>
                      
                      {/* Assignment Details */}
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                        <div>
                          <span className="font-medium">Assigned by:</span> {assignment.assignedByUserName}
                        </div>
                        <div>
                          <span className="font-medium">Reason:</span> {assignment.assignmentReason}
                        </div>
                      </div>

                      {/* FIXED: Return Details - Show when assignment is returned */}
                      {assignment.status === 'Returned' && (
                        <div className="border-t border-green-200 pt-3 mt-3">
                          <div className="flex items-center mb-2">
                            <RotateCcw className="w-4 h-4 text-green-600 mr-2" />
                            <span className="font-medium text-green-800">Return Information</span>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {(assignment as any).returnedAt && (
                              <div>
                                <span className="font-medium text-green-700">Returned Date:</span>
                                <div className="text-green-800">{formatDateTime((assignment as any).returnedAt)}</div>
                              </div>
                            )}
                            {(assignment as any).returnedBy && (
                              <div>
                                <span className="font-medium text-green-700">Returned By:</span>
                                <div className="text-green-800">{(assignment as any).returnedBy}</div>
                              </div>
                            )}
                            {(assignment as any).returnCondition && (
                              <div>
                                <span className="font-medium text-green-700">Return Condition:</span>
                                <div className="text-green-800">{(assignment as any).returnCondition}</div>
                              </div>
                            )}
                            {(assignment as any).returnNotes && (
                              <div className="col-span-2">
                                <span className="font-medium text-green-700">Return Notes:</span>
                                <div className="text-green-800">{(assignment as any).returnNotes}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm">No Assignment History</p>
                  <p className="text-xs text-gray-400">This item has never been assigned</p>
                </div>
              )}
            </div>
          )}

          {/* Body Armor Specific Information */}
          {'bodyArmor' in item && (item as any).bodyArmor && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-amber-800 mb-3 flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                Body Armor Specifications
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-amber-700">Ballistic Level</label>
                  <p className="text-amber-800 font-semibold">{(item as any).bodyArmor.ballisticLevel}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-amber-700">Size</label>
                  <p className="text-amber-800">{(item as any).bodyArmor.size}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-amber-700">Expiration Date</label>
                  <p className="text-amber-800">{formatDate((item as any).bodyArmor.expirationDate)}</p>
                </div>
                {(item as any).bodyArmor.lastInspectionDate && (
                  <div>
                    <label className="block text-sm font-medium text-amber-700">Last Inspection</label>
                    <p className="text-amber-800">{formatDate((item as any).bodyArmor.lastInspectionDate)}</p>
                  </div>
                )}
                {(item as any).bodyArmor.nextInspectionDue && (
                  <div>
                    <label className="block text-sm font-medium text-amber-700">Next Inspection Due</label>
                    <p className="text-amber-800">{formatDate((item as any).bodyArmor.nextInspectionDue)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Purchase Information & Audit Trail */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Purchase Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-600">Purchase Date</label>
                <p className="text-gray-800 flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                  {formatDate(item.purchaseDate)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600">Purchase Price</label>
                <p className="text-gray-800">{formatCurrency(item.purchasePrice)}</p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Audit Trail</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-600">Created</label>
                <p className="text-gray-800">{formatDateTime(item.createdAt)}</p>
                <p className="text-sm text-gray-500">by {item.createdBy}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600">Last Updated</label>
                <p className="text-gray-800">{formatDateTime(item.updatedAt)}</p>
                <p className="text-sm text-gray-500">by {item.updatedBy}</p>
              </div>
            </div>
          </div>

          {/* Photos Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Photos</h3>
            {item.photos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {item.photos.map((photo, index) => (
                  <div key={index} className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                    <Package className="w-8 h-8 text-gray-400" />
                    <p className="text-xs text-gray-500 mt-2">Photo {index + 1}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic">No photos uploaded</p>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => onEdit(item)}
            className="px-4 py-2 text-white rounded-lg transition-colors"
            style={{ backgroundColor: '#082759' }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0a2e6b'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#082759'}
          >
            Edit Item
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemDetailModal;
